import { t } from '@lingui/macro'
import { useCallback, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom-original'

import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { NotFoundError } from '@/modules/shared/components/not-found-error/NotFoundError'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isIntramuros } from '@/utils/intramuros'
import { CloseAmendFlowModal } from '../../components/close-amend-flow-modal/CloseAmendFlowModal'
import { Questionnaire } from '../../components/questionnaire/Questionnaire'
import { invalidFirstStrategy } from '../../hooks/questionnaire-flow-strategy'
import { useAmendProgress } from '../../hooks/useAmendProgress'
import { useAnimationDirection } from '../../hooks/useAnimationDirection'
import { useQuestionnaireCareplanActivity } from '../../hooks/useQuestionnaireCareplanActivity'
import { type QuestionnaireFlowResult, useQuestionnaireFlow } from '../../hooks/useQuestionnaireFlow'
import { QuestionnaireEngineProvider } from '../../providers/QuestionnaireEngineProvider'

import type { QuestionAnswerInput } from '@healthblocks-io/core'

type FlowVariant = 'reconfirmation' | 'prefill'

export function QuestionnaireAmendFlowPage({ variant = 'reconfirmation' }: { variant?: FlowVariant }) {
  // Hooks
  // ------------------------------------------------------------------------- /
  useHideNativeNavigation()
  const { activityId } = useParams<{ activityId: string }>()
  const navigate = useNavigate()
  const [searchParameters] = useSearchParams()

  const { data: activity, isLoading, mutate } = useQuestionnaireCareplanActivity('' + activityId)

  // Template variables
  // ------------------------------------------------------------------------- /
  const initialLinkId = searchParameters.get('linkId')

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleCancel = () => {
    navigate(-1)
  }

  const handleConfirm = () => {
    navigate(-1)
  }

  // Render
  // ------------------------------------------------------------------------- /
  if (isLoading) return <AnimatedLoader />

  if (!activity || !activity.questionnaireResponse || !activity.questionnaire) {
    return <NotFoundError onClick={handleCancel} />
  }

  return (
    <QuestionnaireEngineProvider activity={activity} initialLinkId={initialLinkId ?? undefined}>
      <QuestionnaireAmendFlow variant={variant} onCancel={handleCancel} onConfirm={handleConfirm} mutate={mutate} />
    </QuestionnaireEngineProvider>
  )
}

/**
 * QuestionnaireAmendFlow
 *
 * Step-by-step flow component shared by reconfirmation and prefill modes.
 * Guides users through previously answered questions, allowing them to
 * verify and amend answers.
 *
 * Uses the invalidFirstStrategy via useQuestionnaireFlow to navigate
 * to invalid questions first, with wrap-around search.
 *
 * Must be rendered inside <QuestionnaireEngineProvider>.
 */
const QuestionnaireAmendFlow = ({
  variant,
  onCancel,
  onConfirm,
  mutate,
}: {
  variant: FlowVariant
  onCancel: () => void
  onConfirm: () => void
  mutate?: () => Promise<any>
}) => {
  // State
  // ------------------------------------------------------------------------- /
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState(false)

  // Refs
  // ------------------------------------------------------------------------- /
  const pageLayoutMainRef = useRef<HTMLDivElement>(null)

  // Hooks
  // ------------------------------------------------------------------------- /
  const { showErrorToast } = useToast()

  const isLoggedInForMandatee = useLoggedInForMandatee()

  const { state, currentIndex, currentQuestion, canGoBack, setAnswer, goNext, goBack } =
    useQuestionnaireFlow(invalidFirstStrategy)

  const amendProgress = useAmendProgress(state)
  const animationDirection = useAnimationDirection(currentIndex)

  // Template Variables
  // ------------------------------------------------------------------------- /
  const showBackButton = canGoBack

  // Interaction methods
  // ------------------------------------------------------------------------- /

  const handleCancel = () => {
    setShowCloseModal(true)
  }

  const handleDismissCloseModal = () => {
    mixpanel.track(`${variant}_edit_resumed`, {
      questionnaire_slug: state.questionnaire.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })
    setShowCloseModal(false)
  }

  const handleConfirmCloseModal = () => {
    mixpanel.track(`${variant}_edit_abandoned`, {
      questionnaire_slug: state.questionnaire.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })
    onCancel()
  }

  const scrollToTop = () => {
    pageLayoutMainRef?.current?.scrollTo({ top: 0 })
  }

  const handleGoBack = () => {
    scrollToTop()
    goBack()
  }

  const handleFlowResult = useCallback(
    async (result: QuestionnaireFlowResult) => {
      switch (result.type) {
        case 'COMPLETED': {
          await mutate?.()
          onConfirm()
          break
        }
        case 'ERROR': {
          showErrorToast({
            message: t({
              id: `questionnaire-${variant}.error`,
              comment: 'Questionnaire error message',
              message: 'Er ging iets mis bij het aanpassen van de antwoorden',
            }),
          })
        }
      }
    },
    [variant, mutate, onConfirm, showErrorToast],
  )

  const withSaving = useCallback(
    async (action: () => Promise<QuestionnaireFlowResult>) => {
      setIsSaving(true)
      scrollToTop()

      try {
        await handleFlowResult(await action())
      } catch {
        showErrorToast({
          message: t({
            id: 'questionnaire.save-progress.error',
            comment: 'Error saving questionnaire progress',
            message: 'Er ging iets mis bij het opslaan. Probeer het opnieuw.',
          }),
        })
      } finally {
        setIsSaving(false)
      }
    },
    [handleFlowResult, showErrorToast],
  )

  const handleSubmitAnswer = async (answer: QuestionAnswerInput, autoAdvance?: boolean) => {
    await withSaving(() => setAnswer(answer, autoAdvance))
  }

  const handleGoNext = async () => {
    await withSaving(() => goNext())
  }

  // Render
  // ------------------------------------------------------------------------- /

  if (!currentQuestion) {
    return <NotFoundError onClick={onCancel} />
  }

  return (
    <ContentPageLayout
      showHeaderAlways
      mainRef={pageLayoutMainRef}
      header={
        <AppHeader
          primaryAction={
            showBackButton
              ? {
                  label: t({
                    id: 'globals.previous-question',
                    comment: 'Global for previous question',
                    message: 'Vorige vraag',
                  }),
                  icon: ['far', 'chevron-left'],
                  onClick: handleGoBack,
                }
              : undefined
          }
          secondaryAction={{
            label: t({
              id: 'globals.close',
              comment: 'Global for close',
              message: 'Sluiten',
            }),
            icon: ['far', 'close'],
            onClick: handleCancel,
          }}
          progress={amendProgress}
          progressLayout='inline'
        />
      }
    >
      <Questionnaire
        animationDirection={animationDirection}
        currentQuestion={currentQuestion}
        onSubmitAnswer={handleSubmitAnswer}
        onNext={handleGoNext}
        onPrevious={showBackButton ? handleGoBack : undefined}
        isSaving={isSaving}
      />
      <CloseAmendFlowModal
        isVisible={showCloseModal}
        onConfirm={handleConfirmCloseModal}
        onClose={handleDismissCloseModal}
      />
    </ContentPageLayout>
  )
}
